/**
 * RightSidebar.js
 * OnTimeLog 오른쪽 사이드바
 * - DB 연결 상태
 * - To-Do 리스트 내역
 */

import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemText,
  Divider,
  Paper,
  Tooltip,
  Chip,
  Checkbox,
  Button,
  ListItemButton,
  alpha,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CheckSquare, Plus, Clock, Trash2 } from 'react-feather';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { useDBConnection } from '../contexts/DBConnectionContext';
import { useNavigate } from 'react-router-dom';
import apiConfig from '../config/api';

const SIDEBAR_WIDTH = 400; // 사이드바 폭 확장
const COLLAPSED_WIDTH = 56;
const TOP_BAR_HEIGHT = 64;
const DOOR_TRANSITION = 'cubic-bezier(0.26, 0.86, 0.44, 1)';
const DOOR_DURATION = 420;
const API_BASE_URL = apiConfig.BASE;
const API_V1_BASE = apiConfig.V1;

const CONNECTION_INFO_STORAGE_KEY = 'otl_db_connection_info';
const DB_CONNECTION_TOGGLE_KEY = 'otl_db_connection_toggle'; // 사용자 수동 연결 해제 상태

const loadStoredConnectionSettings = () => {
  try {
    const saved = localStorage.getItem(CONNECTION_INFO_STORAGE_KEY);
    if (!saved) {
      return {
        server: '',
        database: '',
        authType: '',
        username: '',
      };
    }
    const parsed = JSON.parse(saved);
    return {
      server: parsed.server ?? '',
      database: parsed.database ?? '',
      authType: parsed.authType ?? '',
      username: parsed.username ?? '',
    };
  } catch {
    return {
      server: '',
      database: '',
      authType: '',
      username: '',
    };
  }
};

const RightSidebar = ({ open, onToggle }) => {
  const { isDark } = useTheme();
  const { isConnected: contextDbConnected } = useDBConnection();
  const navigate = useNavigate();
  
  const [todos, setTodos] = useState([]);
  const [selectedTodos, setSelectedTodos] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  // tasks 상태 제거됨: tasks API가 unused_files로 이동됨 (work 테이블이 V3에서 제거되어 사용 불가)
  // const [tasks, setTasks] = useState([]);
  // 사용자 수동 연결 해제 상태 확인
  const [userDisconnected, setUserDisconnected] = useState(() => {
    try {
      const saved = localStorage.getItem(DB_CONNECTION_TOGGLE_KEY);
      return saved === 'false'; // false면 사용자가 연결 해제한 상태
    } catch {
      return false;
    }
  });
  const [isDbConnected, setIsDbConnected] = useState(() => {
    // 사용자가 연결 해제한 경우 false, 아니면 실제 연결 상태 확인
    try {
      const saved = localStorage.getItem(DB_CONNECTION_TOGGLE_KEY);
      if (saved === 'false') return false;
    } catch {}
    return Boolean(contextDbConnected);
  });
  const [connectionInfo, setConnectionInfo] = useState(() => {
    try {
      const saved = localStorage.getItem(CONNECTION_INFO_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          server: parsed.server ?? '',
          database: parsed.database ?? '',
          authType: parsed.authType ?? '',
          username: parsed.username ?? '',
        };
      }
    } catch {
      //
    }
    return loadStoredConnectionSettings();
  });
  const [connectionLogs, setConnectionLogs] = useState([]);

  // 연결 로그 추가 함수
  const addConnectionLog = React.useCallback((message, level = 'info') => {
    const log = {
      id: `${Date.now()}-${Math.random()}`,
      message,
      level,
      timestamp: new Date().toLocaleTimeString('ko-KR'),
    };
    setConnectionLogs((prev) => {
      const updated = [...prev.slice(-9), log]; // 최대 10개 유지
      return updated;
    });
  }, []);

  // 초기 연결 해제 상태 로그 추가
  useEffect(() => {
    if (userDisconnected && connectionLogs.length === 0) {
      addConnectionLog('MySQL 연결 해제됨 (사용자 요청)', 'info');
    }
  }, [userDisconnected, connectionLogs.length, addConnectionLog]);

  // DB 연결 변경 이벤트 감지 (관리자 페이지와 동기화)
  useEffect(() => {
    const handleDbConnectionChanged = (event) => {
      const detail = event.detail || {};
      if (detail.connected === true) {
        // 연결 정보 업데이트
        const info = {
          server: detail.server || '',
          database: detail.database || '',
          authType: 'mysql',
          username: detail.username || '',
        };
        setConnectionInfo(info);
        setIsDbConnected(true);
        setUserDisconnected(false);
        localStorage.setItem(DB_CONNECTION_TOGGLE_KEY, 'true');
        localStorage.setItem(CONNECTION_INFO_STORAGE_KEY, JSON.stringify(info));
        addConnectionLog(`DB 연결됨: ${info.server}/${info.database}`, 'success');
      } else if (detail.connected === false) {
        setIsDbConnected(false);
        setUserDisconnected(true);
        localStorage.setItem(DB_CONNECTION_TOGGLE_KEY, 'false');
        addConnectionLog('DB 연결 해제됨', 'info');
      }
    };
    
    window.addEventListener('dbConnectionChanged', handleDbConnectionChanged);
    return () => {
      window.removeEventListener('dbConnectionChanged', handleDbConnectionChanged);
    };
  }, [addConnectionLog]);

  // DB 연결 상태 확인 (사용자가 연결 해제하지 않은 경우에만)
  useEffect(() => {
    // 사용자가 연결 해제한 경우 health check 건너뛰기
    if (userDisconnected) {
      return;
    }

    const checkDbHealth = async () => {
      try {
        const response = await axios.get(`/health`);
        const healthData = response.data;
        
        const connected = healthData.database === 'connected';
        setIsDbConnected(connected);
        
        // 연결 정보 업데이트 (관리자 페이지에서 설정한 정보가 없을 때만 기본값 사용)
        if (connected && !connectionInfo.server) {
          const info = {
            server: 'localhost',
            database: 'otl_system',
            authType: 'mysql',
            username: 'root',
          };
          setConnectionInfo(info);
          localStorage.setItem(CONNECTION_INFO_STORAGE_KEY, JSON.stringify(info));
          
          // 연결 성공 로그 추가 (중복 방지)
          const lastLog = connectionLogs[connectionLogs.length - 1];
          if (!lastLog || lastLog.message !== 'MySQL 연결 성공') {
            addConnectionLog('MySQL 연결 성공', 'success');
          }
        } else if (!connected) {
          addConnectionLog(`DB 연결 실패: ${healthData.error || '알 수 없는 오류'}`, 'error');
        }
      } catch (error) {
        setIsDbConnected(false);
        addConnectionLog(`Health check 실패: ${error.message}`, 'error');
      }
    };
    
    checkDbHealth();
    // 헬스체크 간격을 60초로 증가 (불필요한 로그 방지)
    const healthInterval = setInterval(checkDbHealth, 60000); // 60초마다 확인
    return () => clearInterval(healthInterval);
  }, [addConnectionLog, userDisconnected, connectionLogs]);

  // 업체, 계약, Task 목록 조회
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${API_V1_BASE}/admin/customers`);
        setCompanies(response.data || []);
      } catch (error) {
        console.error('업체 목록 조회 실패:', error);
      }
    };
    
    const fetchContracts = async () => {
      try {
        const response = await axios.get(`${API_V1_BASE}/admin/contracts`);
        setContracts(response.data || []);
      } catch (error) {
        console.error('계약 목록 조회 실패:', error);
      }
    };
    
    // fetchTasks 함수 제거됨: tasks API가 unused_files로 이동됨 (work 테이블이 V3에서 제거되어 사용 불가)
    // const fetchTasks = async () => {
    //   try {
    //     const allTasks = [];
    //     const contractsData = await axios.get(`${API_V1_BASE}/admin/contracts`);
    //     for (const contract of contractsData.data || []) {
    //       try {
    //         const response = await axios.get(`${API_V1_BASE}/contracts/${contract.contract_id}/tasks`);
    //         if (response.data && Array.isArray(response.data)) {
    //           allTasks.push(...response.data);
    //         }
    //       } catch (error) {
    //         // 개별 계약의 Task 조회 실패는 무시
    //       }
    //     }
    //     setTasks(allTasks);
    //   } catch (error) {
    //     console.error('Task 목록 조회 실패:', error);
    //   }
    // };
    
    fetchCompanies();
    fetchContracts();
    // fetchTasks(); // 제거됨: tasks API가 unused_files로 이동됨
  }, []);

  // To-Do 리스트 조회
  useEffect(() => {
    const fetchTodos = async () => {
      try {
        const response = await axios.get(`${API_V1_BASE}/todos`);
        setTodos(response.data || []);
      } catch (error) {
        console.error('To-Do 조회 실패:', error);
        setTodos([]);
      }
    };
    
    fetchTodos();
    
    // To-Do 갱신 간격을 120초로 증가 (불필요한 로그 방지)
    const interval = setInterval(fetchTodos, 120000); // 120초(2분)마다 갱신
    
    // To-Do 리스트 업데이트 이벤트 리스너 추가
    const handleTodoListUpdate = () => {
      fetchTodos();
    };
    window.addEventListener('todoListUpdated', handleTodoListUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('todoListUpdated', handleTodoListUpdate);
    };
  }, []);

  const handleTodoToggle = (todoId, e) => {
    // 체크박스 클릭 시에만 선택/해제 처리
    if (e) {
      e.stopPropagation();
    }
    setSelectedTodos(prev => {
      if (prev.includes(todoId)) {
        return prev.filter(id => id !== todoId);
      } else {
        return [...prev, todoId];
      }
    });
  };

  // 개별 To-Do 항목 클릭 시 투입시간 입력 페이지로 이동하고 행 추가
  const handleTodoClick = (todo) => {
    // 투입시간 입력 페이지로 이동
    const isMobileView = typeof document !== 'undefined' && document.body.classList.contains('mobile-view');
    const targetPath = isMobileView ? '/m/log' : '/p/job-time-input';
    navigate(targetPath);
    
    // 페이지 이동 후 이벤트 전송 (약간의 지연을 두어 페이지 로드 대기)
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('addTodosToTimelog', {
        detail: { todos: [todo] }
      }));
    }, 100);
  };

  // To-Do 항목 삭제 (확인 없이 즉시 삭제)
  const handleTodoDelete = async (todo, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    try {
      await axios.delete(`${API_V1_BASE}/todos/${todo.todo_id}`);
      console.log('[RightSidebar] To-Do 삭제 성공:', todo.todo_id);
      
      // To-Do 목록 새로고침
      const response = await axios.get(`${API_V1_BASE}/todos`);
      setTodos(response.data || []);
      
      // To-Do 리스트 업데이트 이벤트 발생 (다른 컴포넌트에 알림)
      window.dispatchEvent(new Event('todoListUpdated'));
    } catch (error) {
      console.error('[RightSidebar] To-Do 삭제 실패:', error);
      alert('To-Do 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 활성 To-Do만 필터링 (완료되지 않은 항목)
  const activeTodos = todos
    .filter(todo => todo.status !== 'completed');

  const handleAddRowsFromTodos = () => {
    if (selectedTodos.length === 0) {
      alert('선택된 To-Do 항목이 없습니다.');
      return;
    }

    const selectedTodoItems = todos.filter(todo => selectedTodos.includes(todo.todo_id));
    
    console.log('[RightSidebar] handleAddRowsFromTodos 호출됨');
    console.log('[RightSidebar] 현재 companies:', companies);
    console.log('[RightSidebar] 현재 contracts:', contracts);
    console.log('[RightSidebar] companies 길이:', companies?.length || 0);
    console.log('[RightSidebar] contracts 길이:', contracts?.length || 0);
    
    // 현재 경로 확인
    const currentPath = window.location.pathname;
    const isTodoListPage = currentPath.includes('/todo-list') || currentPath.includes('/p/todo-list') || currentPath.includes('/m/todo-list');
    const isJobTimeInputPage = currentPath.includes('/job-time-input') || currentPath.includes('/p/job-time-input') || currentPath.includes('/m/log');
    
    // companies와 contracts 데이터를 이벤트와 함께 전달
    const eventData = {
      todos: selectedTodoItems,
      companies: companies || [], // RightSidebar에서 이미 로드한 업체 목록 (빈 배열 fallback)
      contracts: contracts || [], // RightSidebar에서 이미 로드한 계약 목록 (빈 배열 fallback)
    };
    
    console.log('[RightSidebar] 전달할 eventData:', eventData);
    
    if (isTodoListPage) {
      // TodoList 페이지에 있으면 직접 이벤트 전송
      window.dispatchEvent(new CustomEvent('addTodosToTodoList', {
        detail: eventData
      }));
    } else if (isJobTimeInputPage) {
      // 이미 투입시간 입력 페이지에 있으면 바로 이벤트 전송
      window.dispatchEvent(new CustomEvent('addTodosToTimelog', {
        detail: eventData
      }));
    } else {
      // 다른 페이지에 있으면 투입시간 입력 페이지로 이동
      const isMobileView = typeof document !== 'undefined' && document.body.classList.contains('mobile-view');
      const targetPath = isMobileView ? '/m/log' : '/p/job-time-input';
      navigate(targetPath);
      
      // 페이지 이동 후 이벤트 전송
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('addTodosToTimelog', {
          detail: eventData
        }));
      }, 100);
    }
    
    // 선택 초기화
    setSelectedTodos([]);
  };

  // 전체선택/전체해제 핸들러
  const handleSelectAll = () => {
    const activeTodoIds = activeTodos.map(todo => todo.todo_id);
    const allSelected = activeTodoIds.length > 0 && activeTodoIds.every(id => selectedTodos.includes(id));
    
    if (allSelected) {
      // 전체 해제
      setSelectedTodos([]);
    } else {
      // 전체 선택
      setSelectedTodos(activeTodoIds);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckSquare size={18} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              lineHeight: 1.2,
            }}
          >
            To-Do 리스트
          </Typography>
          {activeTodos.length > 0 && (
            <Chip 
              label={activeTodos.length} 
              size="small" 
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={onToggle}>
          <ChevronRight size={18} />
        </IconButton>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
        {/* To-Do 리스트 */}
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activeTodos.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ 
                    height: 24,
                    fontSize: '0.7rem',
                    px: 1,
                    py: 0.5,
                    minWidth: 'auto',
                  }}
                >
                  {activeTodos.length > 0 && activeTodos.every(todo => selectedTodos.includes(todo.todo_id))
                    ? '전체해제'
                    : '전체선택'}
                </Button>
              )}
              {selectedTodos.length > 0 && (
                <Chip 
                  label={`${selectedTodos.length}개 선택`} 
                  size="small" 
                  color="primary"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            {selectedTodos.length > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Plus size={14} />}
                onClick={handleAddRowsFromTodos}
                sx={{ 
                  height: 24,
                  fontSize: '0.7rem',
                  px: 1,
                  py: 0.5,
                }}
              >
                업무 투입시간으로 행추가
              </Button>
            )}
          </Box>
          
          {activeTodos.length === 0 ? (
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                width: '100%',
                backgroundColor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.02),
              }}
            >
              <Typography variant="body2" color="text.secondary">
                활성 To-Do가 없습니다
              </Typography>
            </Paper>
          ) : (
            <List dense sx={{ width: '100%' }}>
              {activeTodos.map((todo) => {
                const company = companies.find(c => c.company_code === todo.company_code);
                const contract = contracts.find(c => c.contract_id === todo.contract_id);
                const isSelected = selectedTodos.includes(todo.todo_id);
                
                // [업체명 | 계약명 | 업무 구분] 형식으로 표시 (업무 상세내용은 caption에만 표시)
                const displayParts = [];
                if (company?.company_name_kr || todo.company_code) {
                  displayParts.push(company?.company_name_kr || todo.company_code);
                }
                if (contract?.contract_title || todo.contract_id) {
                  displayParts.push(contract?.contract_title || todo.contract_id);
                }
                if (todo.job_category) {
                  displayParts.push(todo.job_category);
                }
                const displayText = displayParts.length > 0 
                  ? displayParts.join(' | ') 
                  : 'To-Do 항목';
                
                return (
                  <ListItemButton
                    key={todo.todo_id}
                    onClick={() => handleTodoClick(todo)}
                    sx={{
                      border: isSelected 
                        ? `2px solid ${isDark ? '#8A7CFF' : '#8A7CFF'}` 
                        : `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                      borderRadius: 1,
                      mb: 0.5,
                      width: '100%',
                      backgroundColor: isSelected
                        ? (isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)')
                        : (isDark ? alpha('#fff', 0.02) : alpha('#000', 0.01)),
                      '&:hover': {
                        backgroundColor: isSelected
                          ? (isDark ? 'rgba(138,124,255,0.2)' : 'rgba(138,124,255,0.15)')
                          : (isDark ? alpha('#fff', 0.05) : alpha('#000', 0.02)),
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleTodoToggle(todo.todo_id, e)}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              color: isDark ? '#E0E0E0' : '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {displayText}
                          </Typography>
                          {todo.note_1 && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '0.7rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {todo.note_1}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Tooltip title="투입시간 입력에 추가">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTodoClick(todo);
                        }}
                        sx={{
                          ml: 1,
                          color: isDark ? '#8A7CFF' : '#8A7CFF',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(138,124,255,0.2)' : 'rgba(138,124,255,0.1)',
                          },
                        }}
                      >
                        <Clock size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTodoDelete(todo, e);
                        }}
                        sx={{
                          ml: 0.5,
                          color: isDark ? '#FF6B6B' : '#F44336',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255,107,107,0.2)' : 'rgba(244,67,54,0.1)',
                          },
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

      </Box>
    </Box>
  );

  const collapsedRail = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1.5,
        gap: 1,
        height: '100%',
        transition: `transform ${DOOR_DURATION}ms ${DOOR_TRANSITION}, opacity ${DOOR_DURATION}ms ease`,
        transform: open ? 'translateX(12px)' : 'translateX(0)',
        opacity: open ? 0 : 1,
        pointerEvents: open ? 'none' : 'auto',
        pl: 1,
      }}
    >
      <Tooltip title="사이드 패널 열기" placement="left">
        <IconButton size="small" onClick={onToggle}>
          <ChevronLeft size={16} />
        </IconButton>
      </Tooltip>
      <Divider flexItem sx={{ my: 1 }} />
      <Tooltip title="To-Do 리스트" placement="left">
        <IconButton
          size="small"
          sx={{
            borderRadius: 2,
            backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04),
            transition: 'background-color 200ms ease, transform 200ms ease',
            '&:hover': {
              backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08),
              transform: 'translateX(-2px)',
            },
          }}
        >
          <CheckSquare size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      variant="permanent"
      open={open}
      sx={{
        display: { xs: 'none', sm: 'block' },
        width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH,
          position: 'fixed', // 레이아웃 흐름에서 분리하여 중복 계산 방지
          right: 0, // 화면 우측에 고정
          top: TOP_BAR_HEIGHT,
          height: `calc(100% - ${TOP_BAR_HEIGHT}px)`,
          borderLeft: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
          backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
          transition: `width ${DOOR_DURATION}ms ${DOOR_TRANSITION}, box-shadow ${DOOR_DURATION}ms ${DOOR_TRANSITION}`,
          boxShadow: open
            ? (isDark ? '-16px 0 44px rgba(0,0,0,0.42)' : '-16px 0 40px rgba(80, 11, 113, 0.18)')
            : (isDark ? '0 10px 24px rgba(0,0,0,0.38)' : '0 10px 26px rgba(80, 11, 113, 0.18)'),
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
        {open ? drawerContent : collapsedRail}
      </Box>
    </Drawer>
  );
};

export default RightSidebar;
